import type {
  Case,
  PaginatedCasesResponse,
  DashboardMetrics,
  Opportunity,
  GroupedListResponse,
  GroupedOpportunity,
  VerificationStatus,
  EvidenceRef,
  Memo,
  MemoClaim,
  Job,
  Analysis,
  CalculatorOutput,
  PrecedentListDTO,
  FeedResponse,
  OppDetailDTO,
  PrecedentSearchResponse,
  SimilarIncidentListResponse,
  EntityProfile,
  TimelineResponse,
  OpportunityReviewRequest,
  DeepAnalysis,
  LimitationRequest,
  LimitationResult,
  LimitationAlert,
  IntelligenceFeedResponse,
  ViolationScanRecord,
  EntityEnrichmentPayload,
  DefendantProfileResponse,
  DefendantResearchSection,
  DefendantMemoResponse,
  SavedSearch,
  SavedSearchListResponse,
  NotificationListResponse,
  SectorDashboardResponse,
  SectorDetail,
  RegulatoryFeedResponse,
  ProvenanceResponse,
  RegulatorKey,
  RegulatoryImpact,
  ChronologyResponse,
  AgentsResponse,
  AgentsRunResponse,
  AgentsSignoffResponse,
} from "@/types";
import type { DeclineReason } from "@/lib/decline-reasons";

export const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

if (!API_BASE) {
  console.warn("API_BASE is empty — API calls will use relative URLs (same-origin)");
}

const TEST_AUTH = process.env.NEXT_PUBLIC_TEST_AUTH;

// Derive display-only fields the backend doesn't yet populate. Plan A
// supplies `litigation_readiness` directly on each group; the UI also
// needs a coarser `verification_status` (drives the Confirmed / Signal /
// Pending pill) and a `verification_basis` line. Both are computed
// client-side until backend coverage extends. 'pending' is reserved for
// the Plan C validator agent — we only synthesize 'confirmed' or 'signal'.
//
// Marina's spec (2026-05-11): the basis line should explain WHY a card
// is verified, not just count sources. We cite ICO enforcement material
// when an ICO-family source is present and append corroborating public
// reporting when at least one credible non-ICO source is also present.
// Raw connector / source-feed names (HIBP, dehashed, etc.) never appear
// — those go through Plan A's source-name scrub on the prose fields and
// never reach this label.
const ICO_SOURCE_TYPES = new Set([
  "ico",
  "ico_breach_dataset",
  "ico_cmp",
  "ico_media",
]);
const REGULATOR_SOURCE_TYPES = new Set([
  "fca_enforcement",
  "cma",
  "ncsc_news",
  "ofcom_enforcement",
]);
const COURT_SOURCE_TYPES = new Set([
  "find_case_law",
  "bailii",
  "cat_cases",
  "hmcts_hearing_lists",
  "gazette",
]);
const NEWS_SOURCE_TYPES = new Set([
  "newsapi",
  "perigon",
  "gdelt",
  "event_registry",
  "legal_news_rss",
]);

function deriveVerificationBasis(
  status: VerificationStatus,
  sourceTypes: string[],
): string {
  const has = (set: Set<string>) => sourceTypes.some((s) => set.has(s));
  const hasIco = has(ICO_SOURCE_TYPES);
  const hasRegulator = has(REGULATOR_SOURCE_TYPES);
  const hasCourt = has(COURT_SOURCE_TYPES);
  const hasNews = has(NEWS_SOURCE_TYPES);
  const count = sourceTypes.length;
  if (status === "confirmed") {
    if (hasIco) {
      return hasNews || hasCourt
        ? "Verified through ICO enforcement material and corroborating public source coverage."
        : "Verified through ICO enforcement material.";
    }
    if (hasRegulator) {
      return hasNews || hasCourt
        ? "Verified through regulatory enforcement material and corroborating public source coverage."
        : "Verified through regulatory enforcement material.";
    }
    if (hasCourt) {
      return "Verified through court / case-law records.";
    }
    return `Verified across ${count} credible public source${count === 1 ? "" : "s"}.`;
  }
  if (hasIco || hasRegulator) {
    return "Regulatory enforcement material present — verification in progress.";
  }
  return `Reporting from ${count} public source${count === 1 ? "" : "s"}.`;
}

function deriveDisplayFields(group: GroupedOpportunity): GroupedOpportunity {
  // The server (post May-2026 status-model split) derives verification_status
  // server-side. The `?? "signal"` is a conservative fallback only for
  // mid-deploy / cached payloads that predate that change — DON'T fall back
  // to `litigation_readiness === "HIGH"`, because HIGH no longer implies
  // "confirmed by the regulator" (that conflation is the thing the split fixed).
  const verification_status: VerificationStatus =
    group.verification_status ?? "signal";
  const verification_basis =
    group.verification_basis
    ?? deriveVerificationBasis(verification_status, group.source_types);
  return { ...group, verification_status, verification_basis };
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setToken(token: string) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    _isRetry = false,
  ): Promise<T> {
    const method = (options.method ?? "GET").toUpperCase();

    // FINDING-002: DELETE with no body would 500 because we always
    // set Content-Type: application/json above and Fastify's JSON
    // parser refuses to parse an empty body with that content type
    // (FST_ERR_CTP_EMPTY_JSON_BODY → 500). For methods that don't
    // need a body, skip the Content-Type header entirely so Fastify
    // doesn't try to parse one.
    const hasBody = options.body != null;
    const headers: Record<string, string> = {
      ...(hasBody || method === "POST" || method === "PUT" || method === "PATCH"
        ? { "Content-Type": "application/json" }
        : {}),
      ...(options.headers as Record<string, string>),
    };

    if (
      options.body == null
      && (method === "POST" || method === "PUT" || method === "PATCH")
    ) {
      options = { ...options, body: "{}" };
    }

    if (this.token) {
      headers["Authorization"] = this.token.startsWith("Test ")
        ? this.token
        : `Bearer ${this.token}`;
    } else if (typeof window !== "undefined") {
      // Token is null — fetch a fresh session from Amplify
      try {
        const { fetchAuthSession } = await import("aws-amplify/auth");
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString();
        if (accessToken) {
          this.token = accessToken;
          headers["Authorization"] = `Bearer ${accessToken}`;
        }
      } catch {
        // Not authenticated or Amplify not configured
      }
    }
    if (!headers["Authorization"] && TEST_AUTH) {
      headers["Authorization"] = TEST_AUTH;
    }

    const res = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    // On 401, clear stale token and retry once with a fresh Amplify session
    if (res.status === 401 && !_isRetry && typeof window !== "undefined") {
      this.token = null;
      return this.request<T>(path, options, true);
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({ message: res.statusText }));
      // P6: surface backend `code` field (ONBOARDING_REQUIRED, StageError,
      // ConcurrencyError, etc.) so callers can branch without parsing
      // status codes alone.
      throw new ApiError(
        res.status,
        body.message || body.error || body.detail || "Request failed",
        typeof body.code === "string" ? body.code : undefined,
        body,
      );
    }

    // FINDING-002: 204 No Content has no body; calling .json() throws
    // "Unexpected end of JSON input". Same for 205 Reset Content. Return
    // undefined for empty responses so DELETE/idempotent-no-content
    // routes don't crash the caller.
    if (res.status === 204 || res.status === 205) {
      return undefined as T;
    }

    return res.json();
  }

  // F5 (plan-eng-review 2026-04-30): cheap onboarding probe.
  // Replaces the previous AuthContext.getMetrics() call. Returns 200
  // {userId, firmId, role, ...} when fully onboarded; auth middleware
  // returns 403 ONBOARDING_REQUIRED for Cognito-without-users-row.
  getMe() {
    return this.request<{
      userId: string;
      firmId: string;
      role: string;
      email: string;
      fullName: string;
      // plan-eng-review A4 2026-05-03: AuthContext blocks discovery
      // fetches until this is a non-empty array, so the wizard's
      // "firm hydrated" state is observable from one place.
      firmClaimTypes: string[];
      needsOnboarding: false;
    }>("/api/me");
  }

  // Dashboard
  getMetrics() {
    return this.request<DashboardMetrics>("/api/dashboard/metrics");
  }

  getDashboardCases(limit = 10) {
    return this.request<Case[]>(`/api/dashboard/cases?limit=${limit}`);
  }

  getIntelligenceFeed(limit = 10) {
    return this.request<IntelligenceFeedResponse>(
      `/api/dashboard/intelligence-feed?limit=${limit}`,
    );
  }

  // LEG-104c P7: accept an invite token. Either UPDATEs an existing
  // users row (firm switch) or INSERTs a new one (brand-new user joining
  // an existing firm via invite — bypasses the new-firm wizard). The
  // backend treats /api/firms/me/invitations/accept as an onboarding
  // route so a verified Cognito identity reaches it without a users row.
  acceptInvitation(token: string) {
    return this.request<{
      firm_id: string;
      role: "analyst" | "counsel" | "admin";
      email: string;
    }>("/api/firms/me/invitations/accept", {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  }

  // List active (unaccepted, unexpired) invitations for the caller's
  // firm. Admin-only on the backend (requireRole("admin")).
  listInvitations() {
    return this.request<{
      data: Array<{
        id: string;
        email: string;
        role: "analyst" | "counsel" | "admin";
        expires_at: string;
        created_at: string;
        invited_by_user_id: string;
      }>;
    }>("/api/firms/me/invitations");
  }

  // Issue a new invitation. Admin-only. Returns 201 with accept_url
  // OR 409 INVITE_DUPLICATE if a pending invite already exists for
  // {firm, email}. Frontend catches 409 and offers Resend inline.
  issueInvitation(input: {
    email: string;
    role: "analyst" | "counsel" | "admin";
  }) {
    return this.request<{
      invitation_id: string;
      email: string;
      role: "analyst" | "counsel" | "admin";
      expires_at: string;
      accept_url: string;
    }>("/api/firms/me/invitations", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  // Rotate the invitation's nonce + JWT, refresh Cognito, send a fresh
  // email. Old accept_url stops working immediately. Admin-only.
  resendInvitation(invitationId: string) {
    return this.request<{
      invitation_id: string;
      email: string;
      role: "analyst" | "counsel" | "admin";
      expires_at: string;
      accept_url: string;
    }>(`/api/firms/me/invitations/${invitationId}/resend`, {
      method: "POST",
    });
  }

  // Hard-delete the invitation row. Audit_log preserves forensic
  // detail. Admin-only. Returns 204 (no body).
  revokeInvitation(invitationId: string) {
    return this.request<void>(`/api/firms/me/invitations/${invitationId}`, {
      method: "DELETE",
    });
  }

  // List users in the caller's firm. Powers the /settings/team
  // members table. Admin-only. Returns alphabetically by full_name.
  listFirmUsers() {
    return this.request<{
      data: Array<{
        id: string;
        email: string;
        full_name: string;
        role: "analyst" | "counsel" | "admin";
        created_at: string;
        updated_at: string;
      }>;
    }>("/api/firms/me/users");
  }

  // LEG-104b P7: complete the onboarding wizard. Creates firm + user
  // atomically on the backend; auth.ts treats /api/onboarding/* as
  // ONBOARDING_PREFIXES so request.cognitoIdentity is populated even
  // before the users row exists.
  completeOnboarding(payload: {
    firm_name: string;
    full_name?: string;
    sra_number?: string | null;
    primary_office?: string | null;
    primary_claim_types: string[];
    firm_size?: "solo" | "2-10" | "10-50" | "50+" | null;
    notification_email?: boolean;
    notification_frequency?: "immediate" | "daily" | "weekly";
    min_viability?: number;
  }) {
    return this.request<{
      firm_id: string;
      user_id: string;
      is_new: boolean;
    }>("/api/onboarding/complete", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // LEG-101 Eng-4: trend-pills cache read. Returns whatever's currently in
  // Redis. Empty pills + null generated_at when the daily job hasn't run yet.
  getTrendPills() {
    return this.request<{
      pills: Array<{
        text: string;
        source_count: number;
        sector: string | null;
        source_types: string[];
      }>;
      generated_at: string | null;
    }>("/api/dashboard/trend-pills");
  }

  // Cases
  listCases(params: Record<string, string | number | undefined> = {}) {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString();
    return this.request<PaginatedCasesResponse>(
      `/api/cases${qs ? `?${qs}` : ""}`
    );
  }

  createCase(data: Record<string, unknown>) {
    return this.request<Case>("/api/cases", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  getCase(id: string) {
    return this.request<Case>(`/api/cases/${id}`);
  }

  updateCase(id: string, data: Record<string, unknown>) {
    return this.request<Case>(`/api/cases/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // Eng-6: optimistic concurrency. If `expectedUpdatedAt` is supplied and
  // the row was modified between the read and this write, the backend
  // returns 409 ConcurrencyError and the kanban DnD reverts with a toast.
  advanceStage(
    caseId: string,
    targetStage: string,
    expectedUpdatedAt?: string,
  ) {
    return this.request<Case>(
      `/api/cases/${caseId}/advance-stage`,
      {
        method: "POST",
        body: JSON.stringify({
          target_stage: targetStage,
          ...(expectedUpdatedAt
            ? { expected_updated_at: expectedUpdatedAt }
            : {}),
        }),
      }
    );
  }

  getGateStatus(caseId: string) {
    return this.request<{
      current_stage: string;
      stage_index: number;
      can_advance: boolean;
      next_stage: string | null;
      blocking_reasons: string[];
    }>(`/api/cases/${caseId}/gate-status`);
  }

  getCaseEvidence(caseId: string) {
    return this.request<EvidenceRef[]>(`/api/cases/${caseId}/evidence`);
  }

  getCaseMemos(caseId: string) {
    return this.request<Memo[]>(`/api/cases/${caseId}/memos`);
  }

  getMemoDetail(caseId: string, memoId: string) {
    return this.request<{
      memo: Memo;
      claims: MemoClaim[];
    }>(`/api/cases/${caseId}/memos/${memoId}`);
  }

  triggerResearch(caseId: string) {
    return this.request<{ job_id: string; status: string }>(
      `/api/cases/${caseId}/research`,
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  triggerMemo(caseId: string) {
    return this.request<{ job_id: string; status: string }>(
      `/api/cases/${caseId}/generate-memo`,
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  // Discovery
  triggerScan(filters: Record<string, unknown>) {
    return this.request<{ job_id: string; status: string }>(
      "/api/discovery/scan",
      {
        method: "POST",
        body: JSON.stringify(filters),
      }
    );
  }

  listFacets() {
    return this.request<{
      sectors: { value: string; label: string }[];
      source_types: { value: string; label: string }[];
      llm_configured: boolean;
    }>("/api/discovery/facets");
  }

  listOpportunities(params: Record<string, string | number | undefined> = {}) {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString();
    return this.request<{ data: Opportunity[]; count: number }>(
      `/api/discovery/opportunities${qs ? `?${qs}` : ""}`
    );
  }

  async listGroupedOpportunities(
    params: Record<string, string | number | undefined> = {},
  ): Promise<GroupedListResponse> {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString();
    const raw = await this.request<GroupedListResponse>(
      `/api/discovery/grouped${qs ? `?${qs}` : ""}`
    );
    return { ...raw, groups: raw.groups.map(deriveDisplayFields) };
  }

  /**
   * P1c — indexed early-signal feed (FeedResponse). Server-side practice-area /
   * tier / chip filtering + SQL chip counts + tier-at-read. Replaces the
   * /grouped + client-side feed-adapter path. Booleans drop when false so the
   * query string carries only active filters.
   */
  getFeed(params: Record<string, string | number | boolean | undefined> = {}) {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== false && v !== "")
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return this.request<FeedResponse>(
      `/api/discovery/feed${qs ? `?${qs}` : ""}`,
    );
  }

  /** P1c — opp-detail DTO (verdict band + Value/Readiness axes). 404 (not visible) throws. */
  getOppDetail(oppId: string) {
    return this.request<OppDetailDTO>(
      `/api/discovery/opportunities/${oppId}/detail`,
    );
  }

  promoteOpportunity(oppId: string, opts?: { note?: string | null }) {
    return this.request<Case>(
      `/api/discovery/opportunities/${oppId}/promote`,
      { method: "POST", body: JSON.stringify({ note: opts?.note ?? null }) }
    );
  }

  /**
   * Triage a signal. Layer-3 moat: a `dismissed` call MUST carry a
   * `reason` (a DeclineReason) — the backend rejects it otherwise. `watching`
   * and `dismissed` may also carry an optional free-text `note`.
   */
  triageOpportunity(
    oppId: string,
    status: "new" | "watching" | "dismissed",
    opts?: { reason?: DeclineReason; note?: string | null },
  ) {
    return this.request<{ id: string; triage_status: string }>(
      `/api/discovery/opportunities/${oppId}/triage`,
      {
        method: "PATCH",
        body: JSON.stringify({
          status,
          ...(opts?.reason ? { reason: opts.reason } : {}),
          ...(opts?.note != null ? { note: opts.note } : {}),
        }),
      }
    );
  }

  /**
   * Generate an AI Briefing for a single opportunity (Lane B sourced-AI).
   * Synchronous — waits for the LLM call (3-5s). Returns the enriched opp
   * on success, or a status-only payload on budget_exhausted / thin_sources
   * / error.
   */
  /**
   * `force` (added 2026-05-20 per /plan-eng-review T1d): when true, the
   * backend bypasses the already-enriched guard and re-runs the LLM. Used
   * by the user-facing "Refresh briefing" button — without `force`, the
   * route returns `skipped_already_enriched` and the stale prose persists.
   * The daily budget cap still applies on the backend.
   */
  generateBriefing(oppId: string, opts: { force?: boolean } = {}) {
    const query = opts.force ? "?force=true" : "";
    return this.request<{
      status:
        | "completed"
        | "skipped_already_enriched"
        | "budget_exhausted"
        | "thin_sources"
        | "rate_limited"
        | "parse_failed"
        | "validation_failed"
        | "error";
      opportunity?: Opportunity;
      error?: string | null;
    }>(`/api/discovery/opportunities/${oppId}/briefing${query}`, { method: "POST" });
  }

  getOpportunityGroup(oppId: string) {
    return this.request<GroupedOpportunity>(
      `/api/discovery/opportunities/${oppId}/group`
    );
  }

  /** Plan C: chronology projection used by the Overview tab timeline. */
  getOpportunityChronology(oppId: string) {
    return this.request<ChronologyResponse>(
      `/api/discovery/opportunities/${oppId}/chronology`
    );
  }

  /** Provenance for an enriched opportunity card. Optionally filter to a single AI block. */
  getOpportunitySources(
    oppId: string,
    block?: "narrative" | "legal_basis" | "key_actions" | "key_risks",
  ) {
    const qs = block ? `?block=${block}` : "";
    return this.request<ProvenanceResponse>(
      `/api/discovery/opportunities/${oppId}/sources${qs}`,
    );
  }

  /**
   * P1d — grounded precedents (PrecedentListDTO §1c). Connector-backed; every
   * matter carries a VERIFIED source_url. `status` drives the honest empty
   * state ("none_yet" / "unavailable") — never a fabricated or unlinkable cite.
   */
  getOpportunityPrecedents(oppId: string) {
    return this.request<PrecedentListDTO>(
      `/api/discovery/opportunities/${oppId}/precedents`
    );
  }

  getSimilarIncidents(oppId: string) {
    return this.request<SimilarIncidentListResponse>(
      `/api/discovery/opportunities/${oppId}/similar-incidents`
    );
  }

  getEntityProfile(oppId: string) {
    return this.request<EntityProfile>(
      `/api/discovery/opportunities/${oppId}/entity-profile`
    );
  }

  searchPrecedents(oppId: string) {
    return this.request<PrecedentSearchResponse>(
      `/api/discovery/opportunities/${oppId}/search-precedents`,
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  analyzeOpportunity(oppId: string) {
    return this.request<DeepAnalysis>(
      `/api/discovery/opportunities/${oppId}/analyze`,
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  getEntityEnrichment(oppId: string) {
    return this.request<EntityEnrichmentPayload | null>(
      `/api/discovery/opportunities/${oppId}/entity-profile/enrichment`
    );
  }

  enrichOpportunity(oppId: string) {
    return this.request<EntityEnrichmentPayload>(
      `/api/discovery/opportunities/${oppId}/enrich`,
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  /* ─── Part B — adversarial agents (Investigator → Defendant Counsel → Judge) ─── */

  /** Read the current (non-superseded) agent runs for an opportunity. */
  getAgents(oppId: string) {
    return this.request<AgentsResponse>(
      `/api/discovery/opportunities/${oppId}/agents`
    );
  }

  /** Run the adversarial pipeline on-demand (force). Synchronous (~tens of secs). */
  runAgents(oppId: string) {
    return this.request<AgentsRunResponse>(
      `/api/discovery/opportunities/${oppId}/agents`,
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  /** Counsel/admin sign-off on the Judge verdict (draft → signed). */
  signoffAgents(oppId: string) {
    return this.request<AgentsSignoffResponse>(
      `/api/discovery/opportunities/${oppId}/agents/signoff`,
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  reviewOpportunity(oppId: string, body: OpportunityReviewRequest) {
    return this.request<Opportunity>(
      `/api/discovery/opportunities/${oppId}/review`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );
  }

  getCaseTimeline(caseId: string, limit = 50) {
    return this.request<TimelineResponse>(
      `/api/cases/${caseId}/timeline?limit=${limit}`
    );
  }

  // Analysis
  runAnalysis(type: string, caseId: string) {
    return this.request<{ job_id: string; status: string }>(
      `/api/analysis/${type}`,
      {
        method: "POST",
        body: JSON.stringify({ case_id: caseId }),
      }
    );
  }

  getAnalyses(caseId: string) {
    return this.request<Analysis[]>(`/api/analysis/${caseId}`);
  }

  // Calculator
  computeProfit(data: Record<string, unknown>) {
    return this.request<CalculatorOutput>("/api/calculator/compute", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  calculateLimitation(data: LimitationRequest) {
    return this.request<LimitationResult>("/api/calculator/limitation", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  getLimitationAlerts() {
    return this.request<LimitationAlert[]>("/api/dashboard/limitation-alerts");
  }

  // Entity Investigation
  entityAutocomplete(query: string) {
    return this.request<{ suggestions: { defendant: string; company_number: string | null }[] }>(
      `/api/discovery/entities/autocomplete?q=${encodeURIComponent(query)}`
    );
  }

  getEntityData(entityName: string) {
    return this.request<{ entity_name: string; research: unknown[]; recent_investigations: unknown[] }>(
      `/api/discovery/entities/${encodeURIComponent(entityName)}`
    );
  }

  saveEntityNotes(entityName: string, notes: string, companyNumber?: string) {
    return this.request<unknown>(
      `/api/discovery/entities/${encodeURIComponent(entityName)}/notes`,
      {
        method: "PATCH",
        body: JSON.stringify({ notes, company_number: companyNumber }),
      }
    );
  }

  // Defendant Intelligence
  getDefendantProfile(companyNumber: string) {
    return this.request<DefendantProfileResponse>(
      `/api/defendants/${encodeURIComponent(companyNumber)}`,
    );
  }

  runDefendantResearch(companyNumber: string) {
    return this.request<{
      research_results: DefendantResearchSection[];
      profile: DefendantProfileResponse;
    }>(`/api/defendants/${encodeURIComponent(companyNumber)}/research`, {
      method: "POST",
    });
  }

  async getDefendantMemo(
    companyNumber: string,
  ): Promise<DefendantMemoResponse | null> {
    const res = await this.request<DefendantMemoResponse | { memo: null }>(
      `/api/defendants/${encodeURIComponent(companyNumber)}/memo`,
    );
    // The API returns { memo: null } when no memo exists for this defendant
    // (rather than 404, which would be logged as an error in DevTools).
    if (res && "memo" in res && res.memo === null) return null;
    return res as DefendantMemoResponse;
  }

  generateDefendantMemo(companyNumber: string) {
    return this.request<DefendantMemoResponse>(
      `/api/defendants/${encodeURIComponent(companyNumber)}/memo`,
      { method: "POST" },
    );
  }

  // Saved Searches
  listSavedSearches() {
    return this.request<SavedSearchListResponse>("/api/saved-searches");
  }

  createSavedSearch(data: { name: string; filter_params: Record<string, unknown> }) {
    return this.request<SavedSearch>("/api/saved-searches", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // LEG-98 P5: PATCH endpoint accepts {name?, filter_params?, enabled?}.
  // Used for pause/resume toggle and rename/edit. Backend validates
  // min_viability >= 0.3 (notification flood floor).
  updateSavedSearch(
    id: string,
    data: {
      name?: string;
      filter_params?: Record<string, unknown>;
      enabled?: boolean;
    },
  ) {
    return this.request<SavedSearch>(
      `/api/saved-searches/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
  }

  deleteSavedSearch(id: string) {
    return this.request<void>(`/api/saved-searches/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  // Notifications
  listNotifications(opts?: { unread?: boolean; limit?: number }) {
    const params = new URLSearchParams();
    if (opts?.unread) params.set("unread", "1");
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return this.request<NotificationListResponse>(
      `/api/notifications${qs ? `?${qs}` : ""}`,
    );
  }

  markNotificationSeen(id: string) {
    return this.request<void>(
      `/api/notifications/${encodeURIComponent(id)}/seen`,
      { method: "PATCH" },
    );
  }

  markAllNotificationsSeen() {
    return this.request<{ marked: number }>("/api/notifications/seen-all", {
      method: "PATCH",
    });
  }

  // Sectors
  getSectorDashboard() {
    return this.request<SectorDashboardResponse>("/api/sectors/dashboard");
  }

  getSectorDetail(sector: string) {
    return this.request<SectorDetail>(
      `/api/sectors/${encodeURIComponent(sector)}`,
    );
  }

  // Regulatory Tracker
  getRegulatoryFeed(params: {
    regulators?: RegulatorKey[];
    impact?: RegulatoryImpact[];
    since?: string;
    until?: string;
    category?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }) {
    const qs = new URLSearchParams();
    if (params.regulators?.length)
      qs.set("regulators", params.regulators.join(","));
    if (params.impact?.length) qs.set("impact", params.impact.join(","));
    if (params.since) qs.set("since", params.since);
    if (params.until) qs.set("until", params.until);
    if (params.category) qs.set("category", params.category);
    if (params.search) qs.set("search", params.search);
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    const qstr = qs.toString();
    return this.request<RegulatoryFeedResponse>(
      `/api/regulatory/feed${qstr ? `?${qstr}` : ""}`,
    );
  }

  getConnectorHealth() {
    return this.request<{
      state: "healthy" | "degraded" | "stale" | "unknown";
      total: number;
      healthy: number;
      degraded: number;
      stale: number;
      last_updated: string | null;
    }>(`/api/connectors/health`);
  }

  getConnectorDetails() {
    return this.request<{
      connectors: Array<{
        connector_name: string;
        status: string;
        finished_at: string | null;
        successes_24h: number;
        total_24h: number;
        success_rate_24h: number | null;
        last_error_message: string | null;
        last_alerted_at: string | null;
      }>;
    }>(`/api/connectors/details`);
  }

  researchEntity(entityName: string, opts?: { company_number?: string; sector?: string; legal_basis?: string }) {
    return this.request<{ items: { source_url: string; snippet: string; source_type: string }[]; mode: string; entity_name: string }>(
      `/api/discovery/entities/${encodeURIComponent(entityName)}/research`,
      {
        method: "POST",
        body: JSON.stringify(opts ?? {}),
      }
    );
  }

  trackEntityInvestigation(entityName: string, companyNumber?: string) {
    return this.request<unknown>(
      `/api/discovery/entities/${encodeURIComponent(entityName)}/track`,
      {
        method: "POST",
        body: JSON.stringify({ company_number: companyNumber }),
      }
    );
  }

  getRecentInvestigations() {
    return this.request<{ investigations: { id: string; entity_name: string; company_number: string | null; last_accessed_at: string; outcome: string }[] }>(
      `/api/discovery/investigations/recent`
    );
  }

  // Jobs
  getJob(jobId: string) {
    return this.request<Job>(`/api/jobs/${jobId}`);
  }

  cancelJob(jobId: string) {
    return this.request<Job>(`/api/jobs/${jobId}/cancel`, {
      method: "POST",
    });
  }

  getConnectorFreshness() {
    return this.request<ConnectorFreshness[]>("/api/connectors/freshness");
  }

  // Violations
  scanViolations(data: { url?: string; text?: string }) {
    return this.request<{ scan_id: string; job_id: string; status: string }>(
      "/api/violations/scan",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }

  getScan(scanId: string) {
    return this.request<ViolationScanRecord>(
      `/api/violations/scans/${scanId}`,
    );
  }

  getScanHistory(limit = 20, offset = 0) {
    return this.request<ViolationScanRecord[]>(
      `/api/violations/scans?limit=${limit}&offset=${offset}`,
    );
  }

  async uploadPdfForScan(file: File) {
    const formData = new FormData();
    formData.append("file", file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }
    const TEST_AUTH = typeof window !== "undefined"
      ? (window as unknown as Record<string, string>).__TEST_AUTH
      : undefined;
    if (TEST_AUTH) {
      headers["Authorization"] = TEST_AUTH;
    }

    const res = await fetch(`${this.baseUrl}/api/violations/scans/upload`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({ message: res.statusText }));
      throw new ApiError(res.status, body.message || body.error || "Upload failed");
    }

    return res.json() as Promise<{ scan_id: string; job_id: string; status: string }>;
  }

  createCaseFromScan(scanId: string, violationIndex = 0) {
    return this.request<Case>(
      `/api/violations/scans/${scanId}/create-case?violation=${violationIndex}`,
      { method: "POST" },
    );
  }
}

export interface ConnectorFreshness {
  name: string;
  label: string;
  trust_tier: string;
  freshness: "fresh" | "stale" | "failed" | "never";
  last_sync_at: string | null;
  records_fetched: number | null;
  records_new: number | null;
  error_message: string | null;
}

export class ApiError extends Error {
  status: number;
  // P6: backend's `code` field (e.g. "ONBOARDING_REQUIRED",
  // "StageError", "ConcurrencyError"). Frontend branches on this
  // instead of parsing status codes alone.
  code?: string;
  // Full response body — caller can inspect extras like cognito_identity
  // (returned alongside ONBOARDING_REQUIRED).
  body?: unknown;
  constructor(status: number, message: string, code?: string, body?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export const api = new ApiClient(API_BASE);
